// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// Related projects tab on the location detail page. Lists projects whose
// default location or line item locations include the current location (or
// any of its zones when viewing a site).
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { Sort, invert } from '@/constants/sort';
import { LocationErrorBoundary } from '@/location/components/location-error-boundary';
import { useLocation } from '@/location/hooks/use-location';
import { ProjectFilter, ProjectSortProperty } from '@/project/api/get-projects';
import { ProjectTableView } from '@/project/components/project-table-view';
import { useProjects } from '@/project/hooks/use-projects';
import { Stack } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
  page: z.number().optional(),
});

const PAGE_SIZE = 20;

export const Route = createFileRoute(
  '/_app/locations/$locationKey/related-projects'
)({
  component: RelatedProjectsRoute,
  validateSearch: searchSchema,
});

function RelatedProjectsRoute() {
  const { locationKey } = Route.useParams();

  return (
    <LocationErrorBoundary>
      <RelatedProjectsContent locationKey={locationKey} />
    </LocationErrorBoundary>
  );
}

interface RelatedProjectsContentProps {
  locationKey: string;
}

function RelatedProjectsContent(props: RelatedProjectsContentProps) {
  const searchParameters = Route.useSearch();
  const navigate = Route.useNavigate();
  const { location } = useLocation(props.locationKey);

  const [sort, setSort] = useState<Sort<ProjectSortProperty>>({
    property: 'name',
    direction: 'asc',
  });

  // Match against the location and, when the location is a site, also any of
  // its zones. This keeps behavior consistent with the related assets tab.
  const locationIds = [location.id, ...location.zones.map((z) => z.id)];

  const filter: ProjectFilter = {
    locationIds,
    pageSize: PAGE_SIZE,
    pageNumber: searchParameters.page ?? 1,
    isArchived: 'all',
  };

  const {
    data: projects = [],
    totalCount,
    isLoading,
  } = useProjects({ sort, filter });

  const handleSort = (property: ProjectSortProperty) => {
    setSort((prevSort) => ({
      property,
      direction:
        prevSort.property === property ? invert(prevSort.direction) : 'asc',
    }));
  };

  const handleChangePage = (page: number) => {
    navigate({ search: { ...searchParameters, page } });
  };

  return (
    <HardfinTabCardWrapper title="Related projects">
      <Stack paddingTop={2}>
        <Suspense>
          <ProjectTableView
            projects={projects}
            totalCount={totalCount}
            sort={sort}
            onSort={handleSort}
            isLoading={isLoading}
            onPageChange={handleChangePage}
            currentPage={searchParameters.page ?? 1}
            pageSize={PAGE_SIZE}
          />
        </Suspense>
      </Stack>
    </HardfinTabCardWrapper>
  );
}
